import {
  createContext,
  useContext,
  useEffect,
  type ReactNode,
} from 'react';

type ToolbarApi = {
  setToolbar: (node: ReactNode | null) => void;
};

type TitleExtraApi = {
  setTitleExtra: (node: ReactNode | null) => void;
};

const IntegratedDrawerToolbarContext = createContext<ToolbarApi | null>(null);
const IntegratedDrawerTitleExtraContext = createContext<TitleExtraApi | null>(
  null,
);

export const IntegratedDrawerToolbarProvider = ({
  toolbarApi,
  titleExtraApi,
  children,
}: {
  toolbarApi: ToolbarApi;
  titleExtraApi: TitleExtraApi;
  children: ReactNode;
}) => (
  <IntegratedDrawerToolbarContext.Provider value={toolbarApi}>
    <IntegratedDrawerTitleExtraContext.Provider value={titleExtraApi}>
      {children}
    </IntegratedDrawerTitleExtraContext.Provider>
  </IntegratedDrawerToolbarContext.Provider>
);

/** Register controls into the AppDrawer header toolbar slot. */
export function useIntegratedDrawerToolbar(
  enabled: boolean,
  toolbar: ReactNode | null,
): void {
  const ctx = useContext(IntegratedDrawerToolbarContext);
  const setToolbar = ctx?.setToolbar;
  useEffect(() => {
    if (!setToolbar) return;
    if (!enabled || toolbar == null) {
      setToolbar(null);
      return;
    }
    setToolbar(toolbar);
    return () => {
      setToolbar(null);
    };
  }, [enabled, toolbar, setToolbar]);
}

/** Register controls beside the integrated drawer title. */
export function useIntegratedDrawerTitleExtra(
  enabled: boolean,
  titleExtra: ReactNode | null,
): void {
  const ctx = useContext(IntegratedDrawerTitleExtraContext);
  const setTitleExtra = ctx?.setTitleExtra;
  useEffect(() => {
    if (!setTitleExtra) return;
    if (!enabled || titleExtra == null) {
      setTitleExtra(null);
      return;
    }
    setTitleExtra(titleExtra);
    return () => {
      setTitleExtra(null);
    };
  }, [enabled, titleExtra, setTitleExtra]);
}
